import {
  BookOpenCheck,
  CheckCircle2,
  CircleAlert,
  Database,
  FileSpreadsheet,
  Landmark,
  PackagePlus,
  ReceiptText,
  RotateCcw,
  Scale,
  ShieldCheck,
  ShoppingCart,
  Tags,
  Users,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, InlineNotice, PageHeader, StatusBadge } from "../components/ui";

const workflow = [
  {
    step: 1,
    title: "Thiết lập cửa hàng & thông tin thuế",
    description:
      "Khai báo đúng tên cửa hàng/hộ kinh doanh, địa chỉ, mã số thuế và thông tin vận hành trước khi phát sinh giao dịch.",
    to: "/settings",
    action: "Mở thiết lập",
    icon: Landmark,
  },
  {
    step: 2,
    title: "Tạo Danh mục sản phẩm / SKU",
    description:
      "Mỗi mặt hàng có một SKU ổn định. Khai báo đơn vị cơ sở, quy cách, mã vạch, giá bán, tồn tối thiểu và theo dõi HSD khi cần.",
    to: "/products",
    action: "Mở Danh mục SKU",
    icon: Tags,
  },
  {
    step: 3,
    title: "Khai báo khách hàng & nhà cung cấp",
    description:
      "Chuẩn hóa đối tác để công nợ, lịch sử mua bán và đối soát không bị tách thành nhiều tên khác nhau.",
    to: "/partners",
    action: "Mở đối tác",
    icon: Users,
  },
  {
    step: 4,
    title: "Nhập hàng bằng phiếu nhập",
    description:
      "Nhập đúng SKU, số lượng, giá vốn, nhà cung cấp, phương thức thanh toán, lô/HSD. Xác nhận phiếu mới làm tăng tồn.",
    to: "/inventory/receive",
    action: "Nhập hàng",
    icon: PackagePlus,
  },
  {
    step: 5,
    title: "Bán hàng bằng đơn bán",
    description:
      "Chọn đúng SKU và quy cách. Khi hoàn tất bán hàng, tồn giảm; tiền hoặc công nợ phải thu phát sinh theo phương thức thanh toán.",
    to: "/sales",
    action: "Mở bán hàng",
    icon: ShoppingCart,
  },
  {
    step: 6,
    title: "Theo dõi tiền & công nợ",
    description:
      "Tách rõ tiền thực thu/thực chi với khoản phải thu/phải trả. Không ghi một giao dịch hai lần ở nhiều sổ.",
    to: "/cash",
    action: "Mở Sổ tiền",
    icon: WalletCards,
  },
  {
    step: 7,
    title: "Kiểm kê và xử lý chênh lệch",
    description:
      "Đếm tồn thực tế định kỳ. Chỉ dùng Điều chỉnh tồn cho kiểm kê, hư hỏng, thất thoát, hết hạn hoặc sai lệch có lý do rõ ràng.",
    to: "/inventory",
    action: "Kiểm tra kho",
    icon: Scale,
  },
  {
    step: 8,
    title: "Đối chiếu, đóng sổ & báo cáo",
    description:
      "Đồng bộ dữ liệu, kiểm tra sức khỏe dữ liệu, đối chiếu tiền, đóng sổ ngày rồi mới dùng số liệu cho báo cáo và nghĩa vụ thuế.",
    to: "/closing",
    action: "Đóng sổ ngày",
    icon: BookOpenCheck,
  },
] as const;

const dataRules = [
  {
    title: "Danh mục SKU không phải là tồn kho",
    text: "Tạo SKU chỉ tạo Product Master. Tồn kho chỉ phát sinh từ phiếu nhập, bán hàng, trả hàng hoặc điều chỉnh có kiểm soát.",
    icon: Database,
  },
  {
    title: "Nhập hàng = tăng tồn",
    text: "Nếu thanh toán ngay, tiền giảm. Nếu mua chịu, khoản phải trả nhà cung cấp tăng. Không nhập thêm một phiếu thu/chi trùng giao dịch.",
    icon: PackagePlus,
  },
  {
    title: "Bán hàng = giảm tồn",
    text: "Nếu khách thanh toán ngay, tiền tăng. Nếu bán chịu, khoản phải thu tăng. Doanh thu không đồng nghĩa với tiền đã thu.",
    icon: ShoppingCart,
  },
  {
    title: "Không sửa lịch sử bằng cách ghi đè",
    text: "Sau khi chứng từ đã xác nhận, ưu tiên nghiệp vụ trả hàng/điều chỉnh có lý do thay vì sửa số cũ. Cách này giữ được audit trail.",
    icon: RotateCcw,
  },
  {
    title: "Lô & hạn sử dụng phải đi cùng nghiệp vụ nhập",
    text: "Với hàng có HSD, ghi lô/HSD ngay từ lúc nhập để cảnh báo sắp hết hạn và kiểm kê có căn cứ.",
    icon: CircleAlert,
  },
  {
    title: "Đồng bộ và kiểm tra trước khi chốt số",
    text: "Bảo đảm không còn giao dịch chờ đồng bộ; chạy Kiểm tra dữ liệu trước khi đóng sổ, xuất báo cáo hoặc đối chiếu thuế.",
    icon: ShieldCheck,
  },
] as const;

export function HelpPage() {
  return (
    <div className="page-stack guide-page">
      <PageHeader
        title="Hướng dẫn sử dụng BLedger"
        description="Quy trình vận hành và nguyên tắc nhập dữ liệu để kho, tiền, công nợ, báo cáo và dữ liệu thuế khớp nhau."
        back
      />

      <Card className="guide-hero">
        <div>
          <span className="guide-eyebrow">QUY TRÌNH CHUẨN</span>
          <h2>Nhập đúng một lần, dữ liệu chạy xuyên suốt toàn hệ thống</h2>
          <p>
            BLedger nên được vận hành theo chuỗi: Thiết lập → SKU → Đối tác → Nhập hàng → Bán hàng → Tiền/Công nợ → Kiểm kê → Đóng sổ.
          </p>
        </div>
        <StatusBadge tone="success">Dữ liệu đúng từ đầu</StatusBadge>
        <div className="guide-hero-actions">
          <a className="btn btn-primary" href="#workflow">Xem 8 bước</a>
          <a className="btn btn-secondary" href="#inventory">Quy trình kho</a>
        </div>
      </Card>

      <InlineNotice tone="info" title="Nguyên tắc quan trọng">
        Một nghiệp vụ kinh tế chỉ nên được ghi nhận một lần ở màn hình nghiệp vụ gốc. BLedger sẽ dùng dữ liệu đó để cập nhật tồn, tiền/công nợ và báo cáo liên quan; tránh nhập tay lặp lại cùng giao dịch.
      </InlineNotice>

      <section id="workflow" className="guide-section">
        <div className="section-title">
          <div>
            <h2>Quy trình thực hiện dữ liệu</h2>
            <small>Thực hiện theo thứ tự này khi bắt đầu hoặc khi đào tạo nhân viên mới.</small>
          </div>
        </div>
        <div className="guide-workflow">
          {workflow.map(({ step, title, description, to, action, icon: Icon }) => (
            <Card key={step} className="guide-step">
              <div className="guide-step-index">{step}</div>
              <span className="guide-step-icon"><Icon size={20} aria-hidden /></span>
              <div className="guide-step-body">
                <strong>{title}</strong>
                <p>{description}</p>
                <Link to={to}>{action} →</Link>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="guide-section">
        <div className="section-title">
          <div>
            <h2>6 nguyên tắc để số liệu luôn chính xác</h2>
            <small>Đây là các quy tắc cần thống nhất cho mọi người dùng trong cửa hàng.</small>
          </div>
        </div>
        <div className="guide-rule-grid">
          {dataRules.map(({ title, text, icon: Icon }) => (
            <Card key={title} className="guide-rule">
              <span className="guide-rule-icon"><Icon size={20} aria-hidden /></span>
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section id="inventory" className="guide-section">
        <div className="section-title">
          <div>
            <h2>Quy trình kho hàng chuẩn</h2>
            <small>Dùng khi màn hình Kho chưa có dữ liệu hoặc khi cần xử lý sai lệch tồn.</small>
          </div>
        </div>

        <Card className="guide-flow-card">
          <div className="guide-flow-line">
            <span>1</span>
            <div>
              <strong>Tạo SKU trước</strong>
              <p>Tên hàng, SKU/mã vạch, đơn vị cơ sở, quy cách, giá bán, tồn tối thiểu và HSD.</p>
            </div>
            <Link to="/products">Danh mục SKU</Link>
          </div>
          <div className="guide-flow-line">
            <span>2</span>
            <div>
              <strong>Nhập hàng để phát sinh tồn</strong>
              <p>Phiếu nhập đã xác nhận làm tăng tồn. Có thể nhập trực tiếp hoặc dùng tệp CSV/XLSX sau khi kiểm tra dữ liệu.</p>
            </div>
            <Link to="/inventory/receive">Nhập hàng</Link>
          </div>
          <div className="guide-flow-line">
            <span>3</span>
            <div>
              <strong>Bán hàng để giảm tồn</strong>
              <p>Không giảm tồn bằng điều chỉnh khi thực tế là bán hàng; phải tạo đơn bán để doanh thu, tiền/công nợ và kho cùng khớp.</p>
            </div>
            <Link to="/sales">Bán hàng</Link>
          </div>
          <div className="guide-flow-line">
            <span>4</span>
            <div>
              <strong>Kiểm kê cho sai lệch thực tế</strong>
              <p>Chỉ điều chỉnh khi số đếm thực tế khác hệ thống hoặc có hư hỏng, mất mát, hết hạn. Luôn chọn lý do và ghi chú.</p>
            </div>
            <Link to="/inventory">Kiểm kê kho</Link>
          </div>
        </Card>

        <div className="guide-ledger-grid">
          <Card>
            <PackagePlus size={22} aria-hidden />
            <strong>Nhập hàng</strong>
            <p><b>Kho +</b> · Thanh toán ngay: <b>Tiền −</b> · Mua chịu: <b>Phải trả +</b></p>
          </Card>
          <Card>
            <ShoppingCart size={22} aria-hidden />
            <strong>Bán hàng</strong>
            <p><b>Kho −</b> · Thu ngay: <b>Tiền +</b> · Bán chịu: <b>Phải thu +</b></p>
          </Card>
        </div>
      </section>

      <section className="guide-section">
        <div className="section-title">
          <div>
            <h2>Checklist vận hành hằng ngày</h2>
            <small>Ngắn gọn nhưng đủ để hạn chế sai số tích lũy.</small>
          </div>
        </div>
        <Card className="guide-checklist">
          <div><CheckCircle2 size={19} /><span><strong>Đầu ngày:</strong> kiểm tra trạng thái đồng bộ, cảnh báo tồn thấp/HSD và số dư cần chú ý.</span></div>
          <div><CheckCircle2 size={19} /><span><strong>Trong ngày:</strong> mọi nhập, bán, thu/chi, công nợ phải đi qua đúng nghiệp vụ; không ghi chép trùng.</span></div>
          <div><CheckCircle2 size={19} /><span><strong>Khi có sai lệch:</strong> xác định nguyên nhân trước; dùng trả hàng hoặc điều chỉnh có lý do, không sửa số để “cho khớp”.</span></div>
          <div><CheckCircle2 size={19} /><span><strong>Cuối ngày:</strong> đồng bộ → Kiểm tra dữ liệu → đối chiếu két/Sổ tiền → Đóng sổ ngày.</span></div>
          <div><CheckCircle2 size={19} /><span><strong>Định kỳ:</strong> kiểm kê kho, rà soát công nợ, sao lưu/xuất dữ liệu và kiểm tra báo cáo/thuế.</span></div>
        </Card>
      </section>

      <section className="guide-section">
        <div className="section-title">
          <div>
            <h2>Công cụ kiểm soát</h2>
            <small>Dùng trước khi lấy số liệu làm báo cáo, bàn giao hoặc đối chiếu.</small>
          </div>
        </div>
        <div className="guide-tool-grid">
          <Link className="card guide-tool-link" to="/data-health">
            <ShieldCheck size={22} />
            <div><strong>Kiểm tra dữ liệu</strong><small>Đối chiếu sổ kho, lô/HSD và số dư tiền.</small></div>
          </Link>
          <Link className="card guide-tool-link" to="/data-tools">
            <FileSpreadsheet size={22} />
            <div><strong>Sao lưu & xuất dữ liệu</strong><small>Snapshot và xuất dữ liệu phục vụ đối soát.</small></div>
          </Link>
          <Link className="card guide-tool-link" to="/reports">
            <ReceiptText size={22} />
            <div><strong>Báo cáo</strong><small>Chỉ xem số liệu sau khi đã đối chiếu dữ liệu nguồn.</small></div>
          </Link>
          <Link className="card guide-tool-link" to="/tax">
            <Landmark size={22} />
            <div><strong>Thuế & nghĩa vụ</strong><small>Kiểm tra tình trạng dữ liệu phục vụ nghĩa vụ thuế.</small></div>
          </Link>
        </div>
      </section>
    </div>
  );
}
